import Button from 'components/common/buttons/Button';
import { Lightning } from '@lightningjs/sdk';
import { constants } from 'aliases';
import { translate } from 'support/translate';
import { HoverableComponent } from 'components/common/HoverableComponent';
import ActionsList from 'components/common/ActionsList';
import CheckmarkBullet from 'components/common/CheckmarkBullet';
import { getFontFaceFromStyle } from 'support/textUtils';
import PasswordInputField from '../common/PasswordInputField';
import EmailInputField from '../common/EmailInputField';
import { ttsTagRemoval } from 'support/generalUtils';
import TextLabel from 'components/common/TextLabel';
export interface CreatePasswordContentTemplateSpec
  extends Lightning.Component.TemplateSpec {
  email: string;
  Title: typeof TextLabel;
  UserInputs: typeof ActionsList;
  Terms: typeof TextLabel;
  ActionsList: typeof ActionsList;
}

const EMAIL_REF = 'EmailInput';
const PASSWORD_REF = 'PasswordInput';
const OPT_IN_REF = 'OptIn';

const WIDTH = 684;
const SPACING = 16;

const TITLE_FONT_SIZE = 36;
const TITLE_OFFSET_BOTTOM = 28;
const TERMS_FONT_SIZE = 18;
const TERMS_LINE_HEIGHT = 29;
const BUTTON_HEIGHT = 65;
const BUTTON_FONT_SIZE = 18;

export default class CreatePasswordContent extends HoverableComponent<CreatePasswordContentTemplateSpec> {
  private _Title = this.getByRef('Title')!;
  private _UserInputs = this.getByRef('UserInputs')!;
  private _ActionsList = this.getByRef('ActionsList')!;
  private _Terms = this.getByRef('Terms')!;

  private _OptInCheckmark = this._ActionsList.getItemByRef(
    OPT_IN_REF,
  ) as CheckmarkBullet;

  private _EmailInput = this._UserInputs.getItemByRef(
    EMAIL_REF,
  ) as EmailInputField;

  private _PasswordInput = this._UserInputs.getItemByRef(
    PASSWORD_REF,
  ) as PasswordInputField;

  set email(value: string) {
    this._EmailInput.input = value;
  }

  get announce() {
    return [this._Title.text!.text, ttsTagRemoval(this._Terms.text!.text)];
  }

  static override _template(): Lightning.Component.Template<CreatePasswordContentTemplateSpec> {
    return {
      w: WIDTH,
      flex: {
        direction: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      },
      Title: {
        type: TextLabel,
        flexItem: { marginBottom: TITLE_OFFSET_BOTTOM },
        text: {
          text: translate('account.signup.createAccount'),
          textAlign: 'center',
          fontFace: getFontFaceFromStyle('bold'),
          fontSize: TITLE_FONT_SIZE,
        },
      },
      UserInputs: {
        flex: {
          direction: 'column',
        },
        type: ActionsList,
        w: WIDTH,
        spacing: SPACING,
        index: 1,
        signals: {
          $onHover: true,
        },
        passSignals: {
          $openKeyboard: true,
          $resetToEmailInput: true,
        },
        items: [
          {
            ref: EMAIL_REF,
            type: EmailInputField,
            passSignals: {
              $onHover: '$onHover',
              $emailInputSelected: '$resetToEmailInput',
            },
            signals: { $onHover: '$onHover' },
            zIndex: constants.ui.zIndex.foregroundLayer + 2,
          },
          {
            ref: PASSWORD_REF,
            type: PasswordInputField,
            passSignals: {
              $onHover: '$onHover',
              $openKeyboard: true,
            },
            signals: { $onHover: '$onHover' },
            zIndex: constants.ui.zIndex.foregroundLayer + 2,
          },
        ],
      },
      Terms: {
        type: TextLabel,
        flexItem: { marginTop: SPACING },
        w: WIDTH,
        text: {
          text: translate('account.signup.termsDescription'),
          advancedRenderer: true,
          lineHeight: TERMS_LINE_HEIGHT,
          fontSize: TERMS_FONT_SIZE,
        },
      },
      ActionsList: {
        flex: {
          direction: 'column',
        },
        type: ActionsList,
        spacing: SPACING,
        items: [
          {
            ref: OPT_IN_REF,
            type: CheckmarkBullet,
            label: translate('account.signup.optInCheckmark'),
            minWidth: WIDTH,
            height: BUTTON_HEIGHT,
            fontSize: BUTTON_FONT_SIZE,
            action: '$optInToggled',
            passSignals: { $optInToggled: true },
            zIndex: constants.ui.zIndex.foregroundLayer + 2,
            isSelected: false,
            signals: {
              $onHover: '$onHover',
            },
          },
          {
            type: Button,
            minWidth: WIDTH,
            height: BUTTON_HEIGHT,
            fontSize: BUTTON_FONT_SIZE,
            zIndex: constants.ui.zIndex.foregroundLayer + 2,
            label: translate('account.createPassword.submit'),
            action: '$onSubmitRegistration',
            passSignals: {
              $onSubmitRegistration: true,
            },
            signals: {
              $onHover: '$onHover',
            },
          },
        ],
        passSignals: {
          $onSubmitRegistration: true,
        },
        signals: {
          $onHover: true,
          $optInToggled: true,
          $onSubmitRegistration: true,
        },
      },
    };
  }

  override _onActive() {
    this._setState('UserInputs');
  }

  private async $onSubmitRegistration(event: any) {
    const isValid = await this._PasswordInput.checkValid();
    if (!isValid) return;

    this.signal('$onSubmitRegistration', {
      email: this._EmailInput.input,
      password: this._PasswordInput.input,
      optIn: this._OptInCheckmark.isSelected,
    });
  }

  private $optInToggled(event: any) {
    this._OptInCheckmark.isSelected = !this._OptInCheckmark.isSelected;
  }

  private $onHover(target: any) {
    let state = null;

    if (target.id === this._UserInputs.id) {
      state = 'UserInputs';
    } else if (target.id === this._ActionsList.id) {
      state = 'ActionsList';
    }

    if (state) {
      this._setState(state);
      this.signal('$onContentHovered');
    }
  }

  static override _states() {
    return [
      class UserInputs extends this {
        override _handleDown() {
          this._setState('ActionsList');
        }

        override _getFocused() {
          return this._UserInputs;
        }
      },
      class ActionsList extends this {
        override _handleUp() {
          this._setState('UserInputs');
        }
        override _getFocused() {
          return this._ActionsList;
        }
      },
    ];
  }
}
